import '../styles/components/home.css'
import { getProducts } from '../lib/supabase.js'
import { createProductCard } from '../components/product-card.js'
import { updateMeta, injectJsonLd, clearJsonLd } from '../utils/seo.js'

export async function renderHome(outlet) {
  clearJsonLd()
  updateMeta({
    title: 'Airplaten | Carbon Fiber Knife Sharpening Platen for Belt Grinders',
    description: 'Carbon fiber platens engineered for knife sharpening and blade grinding on belt grinders. Fits 2x42, 2x36, Bucktool. 40% cooler, 3x longer life. Lifetime warranty.',
    canonicalPath: '/',
  })

  injectJsonLd({
    '@context': 'https://schema.org',
    '@type': 'Product',
    'name': 'Airplaten Carbon Fiber Platen',
    'description': 'Carbon fiber composite platen for belt grinders. Engineered for knife sharpening and blade grinding. 40% less heat, 3x longer lifespan than PLA.',
    'brand': { '@type': 'Brand', 'name': 'Airplaten' },
    'image': 'https://www.airplaten.com/images/airplaten-clip.png',
    'url': 'https://www.airplaten.com/',
    'material': 'Carbon Fiber Composite',
    'offers': {
      '@type': 'AggregateOffer',
      'lowPrice': '29.99',
      'highPrice': '129.99',
      'offerCount': '6',
      'priceCurrency': 'CAD',
      'availability': 'https://schema.org/InStock',
      'url': 'https://www.airplaten.com/#/shop',
      'shippingDetails': {
        '@type': 'OfferShippingDetails',
        'shippingDestination': [
          { '@type': 'DefinedRegion', 'addressCountry': 'CA' },
          { '@type': 'DefinedRegion', 'addressCountry': 'US' }
        ]
      }
    }
  }, 'product')

  outlet.innerHTML = buildHomeHTML()
  loadFeaturedProducts()

  document.getElementById('hero-video-facade')?.addEventListener('click', function () {
    this.outerHTML = `<iframe
      src="https://www.youtube.com/embed/ge5duRtX65Y?autoplay=1&loop=1&playlist=ge5duRtX65Y&controls=1&rel=0&modestbranding=1&playsinline=1"
      title="Airplaten"
      frameborder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      style="position:absolute;inset:0;width:100%;height:100%;border:0;transform:scale(2) translateY(-10%);transform-origin:center center;"
    ></iframe>`
  })
}

function buildHomeHTML() {
  return `
    <!-- Hero -->
    <section class="hero">
      <div class="hero-text">
        <p class="hero-eyebrow">Lifetime warranty</p>
        <h1 class="hero-title">Built to<br>outlast<br><em>everything.</em></h1>
        <p class="hero-desc">
          Carbon-fiber composite platen engineered for professionals who can't afford downtime.
        </p>
        <div class="hero-actions">
          <a href="#/shop" class="btn btn-primary btn-lg">Shop Products</a>
          <a href="#/contact" class="btn btn-secondary btn-lg">Get in Touch</a>
        </div>
      </div>
      <div class="hero-image">
        <div id="hero-video-facade" style="position:absolute;inset:0;cursor:pointer;" aria-label="Play video">
          <img
            src="/images/hero-video-thumb.jpg"
            alt="Airplaten carbon fiber platen installed on belt grinder for knife sharpening"
            width="1280" height="720"
            style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;transform:scale(2) translateY(-10%);transform-origin:center center;"
          />
          <div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;pointer-events:none;">
            <div style="width:72px;height:72px;border-radius:50%;background:rgba(255,255,255,0.15);backdrop-filter:blur(4px);border:2px solid rgba(255,255,255,0.4);display:flex;align-items:center;justify-content:center;transition:transform 0.15s,background 0.15s;" id="hero-play-btn">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
                <path d="M8 5v14l11-7z"/>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Trust strip -->
    <div class="trust-strip">
      <div class="trust-strip-inner">
        <div class="trust-item">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 18.75a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 0 1-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 0 0-3.213-9.193 2.056 2.056 0 0 0-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 0 0-10.026 0 1.106 1.106 0 0 1-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
          </svg>
          Canada & US shipping
        </div>
        <div class="trust-item">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
          </svg>
          Secure Stripe checkout
        </div>
        <div class="trust-item">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M11.42 15.17 17.25 21A2.652 2.652 0 0 0 21 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 1 1-3.586-3.586l5.653-4.655m5.8-5.8 1.875-1.875a3.375 3.375 0 0 0-4.773-4.773l-1.875 1.875m5.648 5.648-5.648-5.648" />
          </svg>
          100% carbon reinforced
        </div>
      </div>
    </div>

    <!-- Story 1: Heat -->
    <div class="story-section">
      <div class="story-image">
        <picture>
          <source type="image/webp"
            srcset="/images/airplaten-2-400w.webp 400w, /images/airplaten-2-800w.webp 800w, /images/airplaten-2-1200w.webp 1200w"
            sizes="(max-width: 768px) 100vw, 50vw">
          <img src="/images/airplaten-2.png" alt="Carbon fiber belt grinder platen internal heat dissipation structure showing Airplaten tunnel design" width="1200" height="900" loading="lazy" style="width:100%;height:100%;object-fit:cover;object-position:left center;display:block;">
        </picture>
      </div>
      <div class="story-text">
        <p class="story-number">01 — Heat</p>
        <h2 class="story-title">Stays cool.<br>Works harder.</h2>
        <p class="story-desc">The carbon-fiber infusion with Airplaten tunnel acts as a heat dissipator — pulling thermal energy away from the platen surface and your workpiece.</p>
        <div class="story-stat">
          <span class="story-stat-value">40%</span>
          <span class="story-stat-label">less heat generated</span>
        </div>
      </div>
    </div>

    <!-- Story 2: Life (reversed) -->
    <div class="story-section reverse">
      <div class="story-image">
        <img src="/images/made-to-last.jpg" alt="Airplaten carbon fiber belt grinder platen showing durability and precision construction" width="1092" height="1092" loading="lazy" style="width:100%;height:100%;object-fit:cover;object-position:center;display:block;">
      </div>
      <div class="story-text">
        <p class="story-number">02 — Life</p>
        <h2 class="story-title">Made to<br>Last. Do more.</h2>
        <p class="story-desc">The carbon matrix is built for constant use, it will stand up - unlike PLA or PETG alternatives.</p>
        <div class="story-stat">
          <span class="story-stat-value">3×</span>
          <span class="story-stat-label">longer lifespan</span>
        </div>
      </div>
    </div>

    <!-- Story 3: Precision -->
    <div class="story-section">
      <div class="story-image">
        <picture>
          <source type="image/webp"
            srcset="/images/airplaten-clip-400w.webp 400w, /images/airplaten-clip-800w.webp 800w, /images/airplaten-clip-1200w.webp 1200w"
            sizes="(max-width: 768px) 100vw, 50vw">
          <img src="/images/airplaten-clip.png" alt="Carbon fiber platen clip-on mount for belt grinder knife sharpening — lightweight precision fit" width="1200" height="900" loading="lazy" style="width:100%;height:100%;object-fit:cover;display:block;">
        </picture>
      </div>
      <div class="story-text">
        <p class="story-number">03 — Precision</p>
        <h2 class="story-title">Easy install.<br>Feather weight.</h2>
        <p class="story-desc">Rigid carbon construction make for quick swaps + stays put, reducing vibration and slippage on equipment.</p>
        <div class="story-stat">
          <span class="story-stat-value">100%</span>
          <span class="story-stat-label">Lifetime warranty</span>
        </div>
      </div>
    </div>

    <!-- Featured Products -->
    <section class="featured-section">
      <div class="container">
        <div class="featured-header">
          <h2 class="featured-title">Featured<br>products</h2>
          <a href="#/shop" class="btn btn-secondary">View all →</a>
        </div>
        <div id="featured-products" class="featured-grid">
          <div class="products-loading" style="grid-column: 1/-1">
            <div class="spinner"></div>
            <span class="text-secondary">Loading products…</span>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="home-cta">
      <div class="container">
        <p class="home-cta-eyebrow">Ready to upgrade?</p>
        <h2 class="home-cta-title">Stop buying<br>inferior PLA platens.</h2>
        <p class="home-cta-desc">Ships within 2 business days.</p>
        <div class="home-cta-actions">
          <a href="#/shop" class="btn btn-primary btn-lg">Shop Products</a>
          <a href="#/contact" class="btn btn-secondary btn-lg">Talk to us</a>
        </div>
      </div>
    </section>
  `
}

async function loadFeaturedProducts() {
  const grid = document.getElementById('featured-products')
  if (!grid) return

  try {
    const products = await getProducts()
    const featured = products.slice(0, 3)

    grid.innerHTML = ''
    if (featured.length === 0) {
      grid.innerHTML = '<p class="products-empty text-secondary" style="grid-column:1/-1">No products available yet.</p>'
      return
    }

    featured.forEach(p => grid.appendChild(createProductCard(p)))
  } catch (err) {
    console.error('[home] Failed to load products:', err)
    grid.innerHTML = '<p class="products-empty text-secondary" style="grid-column:1/-1">Unable to load products. Please try again.</p>'
  }
}
